import React from 'react';
import { Box, Chip, Typography } from '@mui/material';

const sectionTitleStyle = {
  fontSize: { xs: '1.8rem', md: '2.8rem' },
  fontWeight: 800,
  lineHeight: 1.15,
  color: '#f8fafc',
};

const bulletStyle = {
  color: '#e2e8f0',
  fontSize: { xs: '1rem', md: '1.2rem' },
  lineHeight: 1.4,
  fontWeight: 500,
  mb: 1.2,
};

const panelBase = {
  borderRadius: '16px',
  border: '1px solid',
  p: { xs: 2.5, md: 3.5 },
  minHeight: { xs: 'auto', md: 330 },
};

const ResilientLogo = ({ alt, sources, size = 54 }) => {
  const [index, setIndex] = React.useState(0);
  const current = sources[index];

  if (!current) {
    return (
      <Box
        sx={{
          width: size,
          height: size,
          borderRadius: '50%',
          display: 'grid',
          placeItems: 'center',
          border: '1px solid rgba(148,163,184,0.6)',
          color: '#e2e8f0',
          fontWeight: 800,
          fontSize: 14,
        }}
      >
        {alt.slice(0, 2).toUpperCase()}
      </Box>
    );
  }

  return (
    <img
      src={current}
      alt={alt}
      onError={() => setIndex((prev) => prev + 1)}
      style={{ width: size, height: size, objectFit: 'contain' }}
    />
  );
};

const ModelEvolutionBlogPage = () => {
  return (
    <Box
      sx={{
        minHeight: '100vh',
        px: { xs: 2, md: 5 },
        py: { xs: 3, md: 5 },
        background:
          'radial-gradient(circle at 12% 10%, rgba(34,211,238,0.18), transparent 35%), radial-gradient(circle at 85% 12%, rgba(168,85,247,0.22), transparent 34%), linear-gradient(180deg, #020617 0%, #0b1530 100%)',
      }}
    >
      <Box sx={{ maxWidth: 1280, mx: 'auto' }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 2,
            mb: 3,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
            <ResilientLogo
              alt="Pomsoft"
              size={162}
              sources={[
                '/pomsoft_dark_background.png',
                '/powered_by_pomsoft.png',
                'https://pomsoft.net/images/pomsoft_dark_background.png',
              ]}
            />
            <Typography sx={{ color: '#f8fafc', fontWeight: 700, fontSize: { xs: '2rem', md: '3.25rem' } }}>
              Pomsoft
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
            <ResilientLogo
              alt="EyeSense.ai"
              size={220}
              sources={['/eyesense-logo-v2.png', '/eyesense-logo.svg']}
            />
            <Typography sx={{ color: '#f8fafc', fontWeight: 700, fontSize: { xs: '2rem', md: '3.25rem' } }}>
              EyeSense.ai
            </Typography>
          </Box>
        </Box>

        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography sx={sectionTitleStyle}>
            From Downloaded ONNX to Owning the Model
          </Typography>
          <Typography
            sx={{
              color: '#e2e8f0',
              fontSize: { xs: '1.2rem', md: '2rem' },
              mt: 1.3,
              fontWeight: 500,
            }}
          >
            EyeSense&apos;s next chapter: train in `.pt`, deploy in ONNX.
          </Typography>
        </Box>

        <Box
          sx={{
            height: 4,
            borderRadius: 999,
            background: 'linear-gradient(90deg, #22d3ee 0%, #a78bfa 100%)',
            mb: 3.5,
          }}
        />

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
            gap: 3,
            mb: 3.5,
          }}
        >
          <Box
            sx={{
              ...panelBase,
              position: 'relative',
              overflow: 'hidden',
              borderColor: 'rgba(34,211,238,0.6)',
              boxShadow: '0 0 24px rgba(34,211,238,0.2)',
              background:
                'radial-gradient(circle at 15% 50%, rgba(34,211,238,0.16), transparent 45%), linear-gradient(180deg, rgba(2,6,23,0.78) 0%, rgba(2,6,23,0.62) 100%)',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 14,
                bottom: 14,
                left: 0,
                width: '5px',
                borderRadius: '0 8px 8px 0',
                background: 'linear-gradient(180deg, rgba(34,211,238,0.95), rgba(34,211,238,0.25))',
                boxShadow: '0 0 20px rgba(34,211,238,0.45)',
              },
              '&::after': {
                content: '""',
                position: 'absolute',
                inset: 0,
                background: 'linear-gradient(110deg, transparent 25%, rgba(255,255,255,0.2) 50%, transparent 75%)',
                transform: 'translateX(-130%)',
                opacity: 0,
                transition: 'transform 850ms ease, opacity 350ms ease',
                pointerEvents: 'none',
              },
              '&:hover::after': {
                transform: 'translateX(130%)',
                opacity: 0.35,
              },
            }}
          >
            <Typography sx={{ color: '#22d3ee', fontSize: '2rem', fontWeight: 800, mb: 2, position: 'relative', zIndex: 1 }}>
              Where We Started
            </Typography>
            <Typography sx={{ ...bulletStyle, position: 'relative', zIndex: 1 }}>- Pretrained ONNX from Roboflow gave us fast time-to-market.</Typography>
            <Typography sx={{ ...bulletStyle, position: 'relative', zIndex: 1 }}>- Deployment was simple and baseline quality was strong.</Typography>
            <Typography sx={{ ...bulletStyle, position: 'relative', zIndex: 1 }}>- But model behavior was not tuned for EyeSense-specific scenes.</Typography>
            <Typography sx={{ ...bulletStyle, position: 'relative', zIndex: 1 }}>- Improvement loops slowed when requirements changed.</Typography>
          </Box>

          <Box
            sx={{
              ...panelBase,
              position: 'relative',
              overflow: 'hidden',
              borderColor: 'rgba(196,181,253,0.65)',
              boxShadow: '0 0 24px rgba(196,181,253,0.18)',
              background:
                'radial-gradient(circle at 85% 50%, rgba(168,85,247,0.16), transparent 45%), linear-gradient(180deg, rgba(2,6,23,0.8) 0%, rgba(2,6,23,0.64) 100%)',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 14,
                bottom: 14,
                right: 0,
                width: '5px',
                borderRadius: '8px 0 0 8px',
                background: 'linear-gradient(180deg, rgba(168,85,247,0.95), rgba(168,85,247,0.25))',
                boxShadow: '0 0 20px rgba(168,85,247,0.45)',
              },
              '&::after': {
                content: '""',
                position: 'absolute',
                inset: 0,
                background: 'linear-gradient(110deg, transparent 25%, rgba(255,255,255,0.18) 50%, transparent 75%)',
                transform: 'translateX(-130%)',
                opacity: 0,
                transition: 'transform 850ms ease, opacity 350ms ease',
                pointerEvents: 'none',
              },
              '&:hover::after': {
                transform: 'translateX(130%)',
                opacity: 0.32,
              },
            }}
          >
            <Typography sx={{ color: '#a78bfa', fontSize: '2rem', fontWeight: 800, mb: 2, position: 'relative', zIndex: 1 }}>
              What Changed
            </Typography>
            <Typography sx={{ ...bulletStyle, position: 'relative', zIndex: 1 }}>- Baseline YOLO `.pt` weights now anchor training.</Typography>
            <Typography sx={{ ...bulletStyle, position: 'relative', zIndex: 1 }}>- Curated datasets come from Hugging Face + Roboflow Universe.</Typography>
            <Typography sx={{ ...bulletStyle, position: 'relative', zIndex: 1 }}>- We train and fine-tune on EyeSense-relevant collections.</Typography>
            <Typography sx={{ ...bulletStyle, position: 'relative', zIndex: 1 }}>- Production artifacts are exported to ONNX with intent.</Typography>
          </Box>
        </Box>

        <Box
          sx={{
            borderRadius: '14px',
            border: '1px solid rgba(148,163,184,0.45)',
            background:
              'linear-gradient(90deg, rgba(2,6,23,0.82) 0%, rgba(30,41,59,0.72) 50%, rgba(2,6,23,0.82) 100%)',
            position: 'relative',
            overflow: 'hidden',
            py: { xs: 2.2, md: 2.8 },
            px: { xs: 2, md: 3.5 },
            textAlign: 'center',
            mb: 3.5,
            '&::before': {
              content: '""',
              position: 'absolute',
              inset: 0,
              background:
                'radial-gradient(circle at 30% 50%, rgba(34,211,238,0.12), transparent 40%), radial-gradient(circle at 70% 50%, rgba(168,85,247,0.12), transparent 40%)',
            },
            '&::after': {
              content: '""',
              position: 'absolute',
              inset: 0,
              background: 'linear-gradient(110deg, transparent 25%, rgba(255,255,255,0.16) 50%, transparent 75%)',
              transform: 'translateX(-130%)',
              opacity: 0,
              transition: 'transform 850ms ease, opacity 350ms ease',
              pointerEvents: 'none',
            },
            '&:hover::after': {
              transform: 'translateX(130%)',
              opacity: 0.3,
            },
          }}
        >
          <Typography sx={{ color: '#f8fafc', fontSize: { xs: '1.35rem', md: '2.1rem' }, fontWeight: 600, lineHeight: 1.35, position: 'relative', zIndex: 1 }}>
            Use `.pt` to build and improve models.
          </Typography>
          <Typography sx={{ color: '#22d3ee', fontSize: { xs: '1.35rem', md: '2.1rem' }, fontWeight: 700, lineHeight: 1.35, position: 'relative', zIndex: 1 }}>
            Use ONNX to ship and run models efficiently.
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
            gap: 3,
            mb: 3.5,
          }}
        >
          <Box sx={panelBase}>
            <Typography sx={{ color: '#67e8f9', fontSize: '1.5rem', fontWeight: 800, mb: 1.4 }}>
              Why It Matters
            </Typography>
            <Typography sx={bulletStyle}>- Higher accuracy for real EyeSense camera conditions.</Typography>
            <Typography sx={bulletStyle}>- Faster iteration on false positives and false negatives.</Typography>
            <Typography sx={bulletStyle}>- Better control over data, labels, and class balance.</Typography>
            <Typography sx={bulletStyle}>- Stronger ownership of long-term model quality.</Typography>
          </Box>

          <Box sx={panelBase}>
            <Typography sx={{ color: '#a78bfa', fontSize: '1.5rem', fontWeight: 800, mb: 1.4 }}>
              New Standard Workflow
            </Typography>
            <Typography sx={bulletStyle}>1. Choose baseline YOLO `.pt`.</Typography>
            <Typography sx={bulletStyle}>2. Assemble and curate EyeSense-relevant data.</Typography>
            <Typography sx={bulletStyle}>3. Train and fine-tune.</Typography>
            <Typography sx={bulletStyle}>4. Validate metrics and edge cases.</Typography>
            <Typography sx={bulletStyle}>5. Export ONNX for production deployment.</Typography>
            <Typography sx={bulletStyle}>6. Monitor and retrain as new data arrives.</Typography>
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: 1.2,
            mb: 2,
            borderRadius: '12px',
            p: 1,
            background:
              'linear-gradient(90deg, rgba(2,6,23,0.72) 0%, rgba(30,41,59,0.6) 50%, rgba(2,6,23,0.72) 100%)',
            border: '1px solid rgba(148,163,184,0.35)',
            position: 'relative',
            overflow: 'hidden',
            '&::after': {
              content: '""',
              position: 'absolute',
              inset: 0,
              background: 'linear-gradient(110deg, transparent 20%, rgba(255,255,255,0.14) 50%, transparent 80%)',
              transform: 'translateX(-130%)',
              opacity: 0,
              transition: 'transform 850ms ease, opacity 350ms ease',
              pointerEvents: 'none',
            },
            '&:hover::after': {
              transform: 'translateX(130%)',
              opacity: 0.25,
            },
          }}
        >
          <Chip label="YOLO .pt training" sx={{ bgcolor: '#22d3ee', color: '#082f49', fontSize: '1rem', fontWeight: 700, px: 1, position: 'relative', zIndex: 1 }} />
          <Chip label="Curated datasets" sx={{ bgcolor: '#818cf8', color: '#eef2ff', fontSize: '1rem', fontWeight: 700, px: 1, position: 'relative', zIndex: 1 }} />
          <Chip label="ONNX deployment" sx={{ bgcolor: '#1e293b', color: '#f8fafc', fontSize: '1rem', fontWeight: 700, px: 1, position: 'relative', zIndex: 1 }} />
          <Chip label="Continuous retraining" sx={{ bgcolor: '#a855f7', color: '#f5f3ff', fontSize: '1rem', fontWeight: 700, px: 1, position: 'relative', zIndex: 1 }} />
        </Box>

        <Box sx={{ textAlign: 'center', mt: 3 }}>
          <Typography sx={{ color: '#67e8f9', fontSize: { xs: '0.95rem', md: '1.15rem' }, opacity: 0.95 }}>
            #ComputerVision #YOLO #ONNX #MLOps #EdgeAI #BuildInPublic
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default ModelEvolutionBlogPage;
